// docs/116 CI5: the pixel painter of the dice table. Everything is drawn at art resolution (one art
// pixel = one canvas pixel) and scaled up by a whole number in CSS, nearest-neighbour, so the dice,
// chips, coins and the table stay crisp. Pure drawing: the component decides what is where and when.
import { bakeHero, bakedHero, KIT_CELL, lookKey, paintHero, tokenLook, type HeroLook } from '../../../../game/heroKit';
import { hashInts } from './dice';
import type { DiePose } from './diceMotion';

export type Ctx = CanvasRenderingContext2D;
export interface Frame { x: number; y: number; w: number; h: number }
export interface Atlas { img: HTMLImageElement; frames: Record<string, Frame> }

// ---- the scene's geometry (art pixels) ------------------------------------------------------------
export const ART_W = 320, ART_H = 180;
/** The table's far edge (players sit behind it) and the near rim (mine). */
export const FAR_Y = 78, NEAR_Y = 180, FELT_FAR = 84, FELT_NEAR = 164;
const FAR_L = 40, FAR_R = 280, NEAR_L = -18, NEAR_R = 338;
export const edgeAt = (y: number): [number, number] => {
  const t = (y - FAR_Y) / (NEAR_Y - FAR_Y);
  return [FAR_L + (NEAR_L - FAR_L) * t, FAR_R + (NEAR_R - FAR_R) * t];
};
const feltEdge = (y: number): [number, number] => { const [l, r] = edgeAt(y); return [l + 8 + (y - FAR_Y) * 0.04, r - 8 - (y - FAR_Y) * 0.04]; };
/** Where the dice fall: the felt's middle (u, v in 0..1). */
export const diceZone = (u: number, v: number) => ({ x: 92 + u * 136, y: 106 + v * 34 });
export const POT = { x: 160, y: 98 };
export const KITTY = { x: 198, y: 95 };
export const CANDLE = { x: 262, y: 100 };
export const GLASS = { x: 58, y: 104 };
/** The far chairs' centres for `n` far seats. */
export function farX(k: number, n: number): number {
  if (n <= 3) return [104, 160, 216][k] ?? 160;
  return [84, 132, 188, 236][k] ?? 160;
}
export const NEAR = { x: 160, y: 176 };
/** Seat colours: their chips and their plate's edge. */
export const SEAT_COL = ['#c8423a', '#3f6fc8', '#3f9a52', '#d9cfb8'];

// ---- helpers --------------------------------------------------------------------------------------
export function px(c: Ctx, x: number, y: number, w: number, h: number, col: string) { c.fillStyle = col; c.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h)); }
export function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(f >= 0 ? v + (255 - v) * f : v * (1 + f))));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => ch(v).toString(16).padStart(2, '0')).join('')}`;
}
const noise = (x: number, y: number, k = 0) => (hashInts(x, y, k) % 1000) / 1000;

// ---- the room behind and the table (baked once) ------------------------------------------------------
export function paintBackdrop(c: Ctx): void {
  // the back wall: dark planks warmed by the candle
  for (let x = 0; x < ART_W; x += 16) {
    const base = shade('#3a2417', (noise(x, 0) - 0.5) * 0.18);
    px(c, x, 0, 16, FAR_Y, base);
    px(c, x, 0, 1, FAR_Y, shade(base, -0.35));
    for (let y = 0; y < FAR_Y; y++) if (noise(x, y, 1) > 0.86) px(c, x + 2 + Math.floor(noise(x, y, 2) * 12), y, 2 + Math.floor(noise(x, y, 3) * 4), 1, shade(base, -0.18));
  }
  // a beam and a shelf with mugs and bottles
  px(c, 0, 8, ART_W, 5, '#24160e'); px(c, 0, 8, ART_W, 1, '#4a301e');
  px(c, 0, 30, ART_W, 3, '#5a3a22'); px(c, 0, 33, ART_W, 1, '#1e120a');
  const items = [[14, 'b'], [24, 'm'], [33, 'm'], [46, 'b'], [56, 'j'], [268, 'm'], [278, 'b'], [290, 'j'], [302, 'm']] as const;
  for (const [x, k] of items) {
    if (k === 'b') { px(c, x, 18, 5, 12, '#2f5a3a'); px(c, x + 1, 14, 3, 4, '#2f5a3a'); px(c, x + 1, 13, 3, 1, '#a0764a'); px(c, x + 1, 19, 1, 8, '#6fa67c'); }
    else if (k === 'm') { px(c, x, 22, 7, 8, '#8b8f98'); px(c, x + 7, 24, 2, 4, '#6d717a'); px(c, x + 1, 23, 1, 6, '#c6cad2'); px(c, x, 21, 7, 1, '#e8e2d0'); }
    else { px(c, x, 20, 8, 10, '#9b5a32'); px(c, x + 1, 19, 6, 1, '#7a4426'); px(c, x + 1, 21, 1, 7, '#c27a4a'); }
  }
  // hanging lantern glow on the wall (the candle's light comes later)
  const g = c.createRadialGradient(160, 40, 8, 160, 40, 170);
  g.addColorStop(0, 'rgba(255,170,90,0.20)'); g.addColorStop(1, 'rgba(0,0,0,0.35)');
  c.fillStyle = g; c.fillRect(0, 0, ART_W, FAR_Y);
}

export function paintTable(c: Ctx): void {
  // the rim: worn boards in perspective
  for (let y = FAR_Y; y < ART_H; y++) {
    const [l, r] = edgeAt(y);
    const t = (y - FAR_Y) / (NEAR_Y - FAR_Y);
    for (let x = Math.max(0, Math.floor(l)); x < Math.min(ART_W, Math.ceil(r)); x++) {
      // grain runs towards the far edge
      const gx = Math.round(160 + (x - 160) / (0.55 + 0.45 * t));
      const plank = Math.floor((gx + 400) / 34);
      let col = shade('#6e4528', (noise(plank, 0, 4) - 0.5) * 0.16);
      if (((gx + 400) % 34) === 0) col = '#3a2113';
      else if (noise(gx, Math.floor(y / 3), 5) > 0.93) col = shade(col, -0.16);
      else if (noise(gx, y, 6) > 0.97) col = shade(col, 0.12);
      c.fillStyle = col; c.fillRect(x, y, 1, 1);
    }
  }
  // the far lip catches the light, the near one is in my shadow
  for (let x = 0; x < ART_W; x++) { const [l, r] = edgeAt(FAR_Y); if (x >= l && x < r) { px(c, x, FAR_Y, 1, 1, '#a8774a'); px(c, x, FAR_Y + 1, 1, 1, '#8a5c36'); } }
  // the felt: worn green baize, an ordered-dither paler patch where the dice land, darker under the rim
  const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
  for (let y = FELT_FAR; y < FELT_NEAR; y++) {
    const [l, r] = feltEdge(y);
    for (let x = Math.ceil(l); x < Math.floor(r); x++) {
      const dx = (x - 160) / 130, dy = (y - 124) / 46;
      const wear = Math.max(0, 1 - Math.sqrt(dx * dx + dy * dy));
      const th = (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16;
      let col = '#284a31';
      if (wear * 1.25 > th + 0.55) col = '#36603d';
      else if (wear * 1.6 > th) col = '#2e5536';
      if (noise(x, y, 7) > 0.992) col = '#3f6c47';
      const edge = Math.min(x - l, r - x, y - FELT_FAR, FELT_NEAR - y);
      if (edge < 1.5) col = '#132818';
      else if (edge < 4) col = (BAYER[(y & 3) * 4 + (x & 3)]! / 16) < (4 - edge) / 3 ? '#1c3922' : col;
      c.fillStyle = col; c.fillRect(x, y, 1, 1);
    }
  }
  // brass studs round the felt
  for (let k = 0; k <= 14; k++) {
    const y = FELT_FAR - 2;
    const [l, r] = edgeAt(y);
    px(c, l + 10 + k * ((r - l - 20) / 14), y, 1, 1, '#d8b060');
  }
  // a tankard's ring and a burn mark
  ring(c, 66, 148, 9, 4, 'rgba(10,25,14,0.55)');
  ring(c, 70, 151, 8, 3, 'rgba(10,25,14,0.35)');
  px(c, 236, 150, 3, 1, '#1d3a24'); px(c, 235, 151, 5, 1, '#203f28');
  // the near rim: my edge of the table, a darker lip with a highlight line
  for (let x = 0; x < ART_W; x++) { px(c, x, FELT_NEAR + 1, 1, 1, '#9a6a40'); }
  px(c, 0, ART_H - 3, ART_W, 3, '#2a170c');
  // a pewter tankard on my right
  tankard(c, 288, 150);
}

function ring(c: Ctx, cx: number, cy: number, rx: number, ry: number, col: string) {
  c.fillStyle = col;
  for (let a = 0; a < 64; a++) { const t = (a / 64) * Math.PI * 2; c.fillRect(Math.round(cx + Math.cos(t) * rx), Math.round(cy + Math.sin(t) * ry), 1, 1); }
}

function tankard(c: Ctx, x: number, y: number) {
  px(c, x - 2, y + 15, 18, 3, 'rgba(0,0,0,0.35)');
  px(c, x, y, 13, 16, '#7d828c');
  px(c, x + 1, y + 1, 2, 14, '#c3c8d0'); px(c, x + 10, y, 3, 16, '#5c6069');
  px(c, x, y + 4, 13, 1, '#5c6069'); px(c, x, y + 12, 13, 1, '#5c6069');
  px(c, x + 13, y + 3, 4, 2, '#6d717a'); px(c, x + 15, y + 4, 2, 8, '#6d717a'); px(c, x + 13, y + 11, 4, 2, '#6d717a');
  px(c, x - 1, y - 2, 15, 3, '#f1e6c8'); px(c, x + 2, y - 3, 7, 1, '#fff7e0'); px(c, x + 3, y + 1, 4, 2, '#e9dcb4');
}

// ---- lights -------------------------------------------------------------------------------------------
export function paintCandle(c: Ctx, ms: number, left: number) {
  const { x, y } = CANDLE;
  const h = 4 + Math.round(12 * Math.max(0, Math.min(1, left)));
  px(c, x - 7, y + 1, 15, 3, '#5a4630'); px(c, x - 6, y + 1, 13, 1, '#b8945a'); px(c, x - 8, y + 4, 17, 1, 'rgba(0,0,0,0.4)');
  px(c, x - 2, y - h, 5, h + 1, '#eadfc2'); px(c, x - 2, y - h, 1, h + 1, '#fff6df'); px(c, x + 2, y - h, 1, h + 1, '#c9b88f');
  px(c, x - 3, y - h + 2, 1, 3, '#eadfc2'); // a drip
  px(c, x, y - h - 1, 1, 1, '#2b2018');
  const f = Math.floor(ms / 110) % 4;
  const fl = ([[0, 0], [1, 0], [0, -1], [-1, 0]] as const)[f]!;
  px(c, x - 1 + fl[0], y - h - 5 + fl[1], 3, 4, '#ffb347'); px(c, x + fl[0], y - h - 7 + fl[1], 1, 3, '#ffe08a'); px(c, x, y - h - 3, 1, 2, '#fff8d8');
}

export function paintGlow(c: Ctx, ms: number, left: number) {
  const h = 4 + Math.round(12 * Math.max(0, Math.min(1, left)));
  const fx = CANDLE.x, fy = CANDLE.y - h - 4;
  const flick = 1 + Math.sin(ms / 170) * 0.03 + Math.sin(ms / 53) * 0.02;
  c.save();
  c.globalCompositeOperation = 'lighter';
  const g = c.createRadialGradient(fx, fy, 2, fx, fy, 120 * flick);
  g.addColorStop(0, 'rgba(255,190,110,0.30)'); g.addColorStop(0.35, 'rgba(255,140,60,0.10)'); g.addColorStop(1, 'rgba(0,0,0,0)');
  c.fillStyle = g; c.fillRect(0, 0, ART_W, ART_H);
  c.restore();
  // the corners fall into the room's dark
  c.save();
  const v = c.createRadialGradient(160, 110, 90, 160, 110, 220);
  v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(8,4,2,0.55)');
  c.fillStyle = v; c.fillRect(0, 0, ART_W, ART_H);
  c.restore();
}

/** The hourglass: `left` 0..1 of the wait, `on` when something is being waited for. */
export function paintGlass(c: Ctx, left: number, on: boolean, urgent: boolean, ms: number) {
  const { x, y } = GLASS;
  px(c, x - 5, y + 12, 12, 2, 'rgba(0,0,0,0.4)');
  px(c, x - 4, y - 1, 10, 2, '#7a4a26'); px(c, x - 4, y + 11, 10, 2, '#7a4a26');
  px(c, x - 4, y + 1, 1, 10, '#5a3418'); px(c, x + 5, y + 1, 1, 10, '#5a3418');
  // the glass
  const glass = 'rgba(200,220,235,0.28)';
  for (let r = 0; r < 4; r++) { px(c, x - 2 + r * 0.5, y + 1 + r, 6 - r, 1, glass); px(c, x - 2 + r * 0.5, y + 10 - r, 6 - r, 1, glass); }
  px(c, x, y + 5, 2, 2, glass);
  const sand = urgent && Math.floor(ms / 250) % 2 ? '#ffcf6a' : '#e0b24a';
  const top = on ? Math.round(4 * left) : 0, bottom = on ? 4 - top : 4;
  for (let r = 0; r < top; r++) px(c, x - 1 + (3 - r) * 0.5 - 0.5, y + 5 - r - 1, 2 + r, 1, sand);
  for (let r = 0; r < bottom; r++) px(c, x - 1 + r * 0.5 - 0.5 + 0.5 - (3 - r) * 0, y + 10 - r, 4 - r, 1, sand);
  if (on && left > 0 && Math.floor(ms / 120) % 2) px(c, x + 0.5, y + 6, 1, 3, sand);
}

// ---- dice ------------------------------------------------------------------------------------------
export const DIE = 13;
const PIPS: Record<number, Array<[number, number]>> = {
  2: [[0, 0], [2, 2]], 3: [[0, 0], [1, 1], [2, 2]], 4: [[0, 0], [2, 0], [0, 2], [2, 2]],
  5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]], 6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};
const IVORY = '#efe5cc', IVORY_HI = '#fffaf0', IVORY_LO = '#c4b28a', INK = '#2a1a12', BLOOD = '#a3241c';

function face(c: Ctx, x: number, y: number, n: number, w: number, h: number) {
  if (n === 1) {
    // the skull: a little red death's head
    const cx = x + Math.floor(w / 2) - 2, cy = y + Math.floor(h / 2) - 2;
    px(c, cx, cy, 5, 3, BLOOD); px(c, cx + 1, cy + 3, 3, 2, BLOOD);
    px(c, cx + 1, cy + 1, 1, 1, IVORY); px(c, cx + 3, cy + 1, 1, 1, IVORY);
    px(c, cx + 2, cy + 4, 1, 1, IVORY);
    return;
  }
  const sx = (w - 2) / 3, sy = (h - 2) / 3;
  for (const [i, j] of PIPS[n] ?? []) px(c, x + 1 + Math.round(i * sx + (sx - 2) / 2 + 0.2), y + 1 + Math.round(j * sy + (sy - 2) / 2 + 0.2), 2, h >= 10 ? 2 : 1, INK);
}

/** A die with its top-left at (x, y). `pose` tumbles it; `marked` rings it in gold (to be pushed). */
export function paintDie(c: Ctx, x: number, y: number, n: number, pose: DiePose, opts: { marked?: boolean; dim?: boolean; glow?: boolean } = {}) {
  x = Math.round(x); y = Math.round(y);
  const S = DIE;
  if (pose === 'edge') {
    // on a corner: a diamond
    const m = Math.floor(S / 2) + 1;
    for (let r = 0; r <= m; r++) {
      const w = r * 2 + 1;
      px(c, x + m - r - 1, y + r - 1, w, 1, r === 0 ? INK : IVORY);
      px(c, x + m - r - 1, y + 2 * m - r - 1, w, 1, r === 0 ? INK : IVORY_LO);
    }
    for (let r = 0; r <= m; r++) { px(c, x + m - r - 2, y + r - 1, 1, 1, INK); px(c, x + m + r, y + r - 1, 1, 1, INK); px(c, x + m - r - 2, y + 2 * m - r - 1, 1, 1, INK); px(c, x + m + r, y + 2 * m - r - 1, 1, 1, INK); }
    px(c, x + m - 2, y + m - 2, 2, 2, n === 1 ? BLOOD : INK);
    return;
  }
  if (pose === 'tilt') {
    // tipped towards me: a short top face and a front face
    const top = 6, front = 6;
    px(c, x - 1, y, S + 2, top + front + 1, INK);
    px(c, x, y + 1, S, top, IVORY); px(c, x, y + 1, S, 1, IVORY_HI);
    px(c, x, y + top + 1, S, front - 1, IVORY_LO);
    face(c, x, y + 1, n, S, top);
    face(c, x, y + top + 1, ((n + 2) % 6) + 1, S, front - 1);
    return;
  }
  if (opts.marked) { px(c, x - 2, y - 2, S + 4, S + 4, '#ffd166'); px(c, x - 1, y - 1, S + 2, S + 2, '#7a4f10'); }
  if (opts.glow) { c.save(); c.globalAlpha = 0.35; px(c, x - 3, y - 3, S + 6, S + 6, '#ffe08a'); c.restore(); }
  // flat: rounded corners, lit from the candle on the right
  px(c, x + 1, y, S - 2, S, INK); px(c, x, y + 1, S, S - 2, INK);
  px(c, x + 1, y + 1, S - 2, S - 2, opts.dim ? shade(IVORY, -0.25) : IVORY);
  px(c, x + 1, y + 1, S - 2, 1, IVORY_HI); px(c, x + S - 2, y + 2, 1, S - 3, IVORY_HI);
  px(c, x + 1, y + S - 2, S - 2, 1, IVORY_LO); px(c, x + 1, y + 2, 1, S - 4, IVORY_LO);
  face(c, x + 1, y + 1, n, S - 2, S - 2);
}

export function paintShadow(c: Ctx, cx: number, y: number, z: number) {
  const w = Math.max(5, Math.round(DIE + 1 - z * 0.3));
  c.save(); c.globalAlpha = Math.max(0.12, 0.45 - z * 0.012);
  px(c, cx - w / 2, y, w, 2, '#08120b'); px(c, cx - w / 2 + 1, y + 2, w - 2, 1, '#08120b');
  c.restore();
}

// ---- coins and chips -----------------------------------------------------------------------------------
const COIN_SPOTS: Array<[number, number]> = (() => {
  const out: Array<[number, number]> = [];
  for (let k = 0; k < 80; k++) {
    const layer = Math.floor(Math.sqrt(k * 0.9));
    const spread = Math.max(2, 13 - layer * 2.2);
    const dx = (noise(k, 1, 31) - 0.5) * 2 * spread;
    const dy = (noise(k, 2, 31) - 0.5) * spread * 0.45 - layer * 1.6;
    out.push([Math.round(dx), Math.round(dy)]);
  }
  return out;
})();

export function paintCoin(c: Ctx, x: number, y: number, k = 0) {
  x = Math.round(x); y = Math.round(y);
  px(c, x, y + 2, 6, 1, '#5e3c0c');
  px(c, x, y, 6, 2, k % 3 === 0 ? '#f2c14e' : '#e0a83a'); px(c, x + 1, y - 1, 4, 1, '#ffe08a');
  px(c, x + 1, y, 2, 1, '#fff2b8');
}

/** A heap of `n` coins around (cx, cy) (drawn back to front). */
export function paintHeap(c: Ctx, cx: number, cy: number, n: number) {
  if (n <= 0) return;
  const m = Math.min(n, COIN_SPOTS.length);
  c.save(); c.globalAlpha = 0.35; px(c, cx - 10 - Math.min(6, m / 6), cy + 2, 20 + Math.min(12, m / 3), 3, '#06100a'); c.restore();
  const order = COIN_SPOTS.slice(0, m).map((p, k) => ({ p, k })).sort((a, b) => a.p[1] - b.p[1]);
  for (const { p, k } of order) paintCoin(c, cx + p[0] - 3, cy + p[1], k);
}

export function coinSpot(k: number): [number, number] { return COIN_SPOTS[k % COIN_SPOTS.length]!; }

/** A stack of `n` poker chips in `col` at (x, y) = its foot. */
export function paintChips(c: Ctx, x: number, y: number, n: number, col: string) {
  x = Math.round(x); y = Math.round(y);
  if (n <= 0) return;
  c.save(); c.globalAlpha = 0.4; px(c, x - 1, y + 1, 10, 2, '#06100a'); c.restore();
  for (let i = 0; i < n; i++) {
    const yy = y - i * 2;
    px(c, x, yy - 1, 8, 2, shade(col, -0.35));
    px(c, x + 2, yy - 1, 1, 2, '#f4efe2'); px(c, x + 5, yy - 1, 1, 2, '#f4efe2');
  }
  const top = y - n * 2;
  px(c, x + 1, top - 1, 6, 1, col); px(c, x, top, 8, 1, col); px(c, x + 3, top, 2, 1, shade(col, 0.5));
}

// ---- people -------------------------------------------------------------------------------------------
const tinted = new Map<string, HTMLCanvasElement>();
/** Draws creature `frame` at (x, y) top-left, `scale`× art, optionally darkened (`dark` 0..1). */
export function paintCreature(c: Ctx, a: Atlas, frame: number, x: number, y: number, scale: number, dark = 0, flip = false) {
  // docs/145: a hero's token paints the hero in their kit (darkened as a creature would be)
  const look = tokenLook(frame);
  if (look) { paintKitHero(c, look, x, y, scale, dark, flip); return; }
  const f = a.frames[`creature_${String(frame).padStart(3, '0')}`];
  if (!f) return;
  let src: CanvasImageSource = a.img, sx = f.x, sy = f.y;
  if (dark > 0 && typeof document !== 'undefined') {
    const key = `${frame}:${dark.toFixed(2)}`;
    let cv = tinted.get(key);
    if (!cv) {
      cv = document.createElement('canvas'); cv.width = f.w; cv.height = f.h;
      const t = cv.getContext('2d')!;
      t.drawImage(a.img, f.x, f.y, f.w, f.h, 0, 0, f.w, f.h);
      t.globalCompositeOperation = 'source-atop';
      t.fillStyle = `rgba(20,10,6,${dark})`; t.fillRect(0, 0, f.w, f.h);
      tinted.set(key, cv);
    }
    src = cv; sx = 0; sy = 0;
  }
  c.save();
  if (flip) { c.translate(Math.round(x) + f.w * scale, 0); c.scale(-1, 1); c.drawImage(src, sx, sy, f.w, f.h, 0, Math.round(y), f.w * scale, f.h * scale); }
  else c.drawImage(src, sx, sy, f.w, f.h, Math.round(x), Math.round(y), f.w * scale, f.h * scale);
  c.restore();
}

/** A chair back behind a seated figure. */
export function paintChair(c: Ctx, cx: number, top: number, empty: boolean) {
  const col = '#4a2c18';
  px(c, cx - 15, top, 30, 4, col); px(c, cx - 15, top, 30, 1, '#6e4528');
  px(c, cx - 15, top, 3, FAR_Y - top, col); px(c, cx + 12, top, 3, FAR_Y - top, col);
  if (empty) { px(c, cx - 12, top + 10, 24, 3, col); px(c, cx - 12, top + 10, 24, 1, '#6e4528'); }
}

/** A hero in their kit, its board cell's top-left at (x, y), darkened like a creature (docs/145). */
function paintKitHero(c: Ctx, look: HeroLook, x: number, y: number, scale: number, dark: number, flip: boolean) {
  if (dark <= 0 || typeof document === 'undefined') { paintHero(c, look, 0, x, y, flip, scale); return; }
  const key = `kit:${lookKey(look)}:${dark.toFixed(2)}`;
  let cv = tinted.get(key);
  if (!cv) {
    const f = bakedHero(look);
    if (!f) { void bakeHero(look); return; }
    cv = document.createElement('canvas'); cv.width = f.a.width; cv.height = f.a.height;
    const t = cv.getContext('2d')!;
    t.drawImage(f.a, 0, 0);
    t.globalCompositeOperation = 'source-atop';
    t.fillStyle = `rgba(20,10,6,${dark})`; t.fillRect(0, 0, cv.width, cv.height);
    tinted.set(key, cv);
  }
  const w = cv.width * scale, h = cv.height * scale, px = x - KIT_CELL.x * scale, py = y - KIT_CELL.y * scale;
  if (!flip) { c.drawImage(cv, px, py, w, h); return; }
  c.save(); c.translate(px + w, py); c.scale(-1, 1); c.drawImage(cv, 0, 0, w, h); c.restore();
}
